import { DomInsertionService, DOM_STRATEGY, StyleContentStrategy } from '@abp/ng.core';
import { Injectable, Renderer2, RendererFactory2, Injector } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { from, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
	GetThemeSettings,
	SetLayoutBoxed,
	SetMenuPlacement,
	SetMenuStatus,
	SetSelectedSettingTab,
	SetStyle,
	UpdateThemeSettings,
} from '../actions/layout.actions';
import { Layout } from '../models/layout';
import { SettingsService } from '../services/settings.service';
import { getMetronicStyle, removeMetronicStyles, loadMetronicStyle } from '../utils/layout.utils';

@State<Layout.State>({
	name: 'MetronicLayoutState',
})
@Injectable()
export class LayoutState {
	renderer: Renderer2;

	constructor(
		private injector: Injector,
		private domInsertionService: DomInsertionService,
		private rendererFactory: RendererFactory2,
		private themeSettingsService: SettingsService,
	) {
		this.renderer = this.rendererFactory.createRenderer(document.body, null);
	}

	private static getLogoColor(propertyName: string): 'dark' | 'light' {
		const styles = getComputedStyle(document.documentElement);
		const logo = styles.getPropertyValue(propertyName);

		return logo.indexOf('dark') > -1 ? 'dark' : 'light';
	}

	@Selector()
	static getPrimaryLogoColor(_: Layout.State) {
		return LayoutState.getLogoColor('--logo');
	}

	@Selector()
	static getSecondaryLogoColor(_: Layout.State) {
		return LayoutState.getLogoColor('--logo-reverse');
	}

	@Selector()
	static getMenuPlacement({ menuPlacement }: Layout.State) {
		return menuPlacement;
	}

	@Selector()
	static getMenuStatus({ menuStatus }: Layout.State) {
		return menuStatus;
	}

	@Selector()
	static getStyle({ style }: Layout.State) {
		return style;
	}

	@Action(SetMenuStatus)
	setCollapsed({ patchState, getState }: StateContext<Layout.State>, { payload }: SetMenuStatus) {
		if (getState().menuStatus === payload) {
			return;
		}


		patchState({
			menuStatus: payload,
		});
	}

	@Action(SetMenuPlacement)
	setPlacement(
		{ patchState, getState }: StateContext<Layout.State>,
		{ payload }: SetMenuPlacement,
	) {
		if (getState().menuPlacement === payload) {
			return;
		}

		if (payload === Layout.MenuPlacement.Top) {
			this.renderer.removeClass(document.body, 'lp-opened-sidebar');
			this.renderer.removeClass(document.body, 'lp-body-fixed');
			this.renderer.removeClass(document.body, 'lp-closed');

			this.renderer.addClass(document.body, 'lp-topmenu');
		} else {
			this.renderer.removeClass(document.body, 'lp-topmenu');
			this.renderer.addClass(document.body, 'lp-opened-sidebar');
		}

		patchState({
			menuPlacement: payload,
		});
	}

	@Action(SetLayoutBoxed)
	boxedLayout({ patchState, getState }: StateContext<Layout.State>, { payload }: SetLayoutBoxed) {
		if (getState().boxedLayout === payload) {
			return;
		}

		if (payload) {
			this.renderer.addClass(document.body, 'lp-boxed');
		} else {
			this.renderer.removeClass(document.body, 'lp-boxed');
		}

		patchState({
			boxedLayout: payload,
		});
	}

	@Action(SetStyle)
	setStyle({ patchState, getState }: StateContext<Layout.State>, { payload }: SetStyle) {
		const { style, styleElement } = getState();
		if (style === payload || typeof payload !== 'number') {
			return;
		}

		return loadMetronicStyle(payload, this.injector).pipe(
			tap(element => {

				removeMetronicStyles(styleElement, this.injector);
				patchState({
					style: payload,
					styleElement: element,
				});
			}),
		);
	}

	@Action(UpdateThemeSettings)
	updateThemeSettings(
		{ getState, patchState, dispatch }: StateContext<Layout.State>,
		{ payload }: UpdateThemeSettings,
	) {
		const state = getState();

		return this.themeSettingsService.update(payload);
	}

	@Action(GetThemeSettings)
	getThemeSettings({ patchState, dispatch }: StateContext<Layout.State>) {
		return this.themeSettingsService.get().pipe(
			tap(themeSettings => {
				dispatch(themeSettings)
			}),
		);
	}

// ------------------------------------------------------------------------

	@Selector()
	static getSelectedTab({ selectedTab }: Layout.State) {
		return selectedTab;
	}

	@Action(SetSelectedSettingTab)
	settingManagementAction(
		{ patchState }: StateContext<Layout.State>,
		{ payload }: SetSelectedSettingTab,
	) {
		patchState({
			selectedTab: payload,
		});
	}

// ------------------------------------------------------------------------

}
